import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Input } from '@dynamika-shared/ui/Input/Input';
import { CloseOutlined } from '@ant-design/icons';
import { FC, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { usePMTaskDefinitionSlice } from '@restore/PMTaskDefinition/PMTaskDefinition.slice';
import { TextArea } from '@dynamika-shared/ui/TextArea/TextArea';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';
import { InputNumber } from '@dynamika-shared/ui/InputNumber/InputNumber';

export type CreateReadingType = {
  name: string;
  code: string;
  visibleForImagingSpecialist: boolean;
  description?: string;
  taskDefinitionOrder: number;
  automaticExecution: boolean;
};

type CreateTaskDrawerProps = {
  open?: boolean;
  onClose: () => void;
  onOkClick: (data: CreateReadingType) => void;
  onCancelClick: () => void;
};

const schema = yup.object().shape({
  name: yup.string().required('Name is required').min(3).max(255),
  code: yup
    .string()
    .required('Code is required')
    .min(3, 'Code must be at least 3 characters')
    .max(255)
    .matches(
      /^[A-Z0-9_]+$/,
      'Code must be in uppercase, contain numbers, and have no spaces'
    ),
  visibleForImagingSpecialist: yup.boolean().required(),
  description: yup.string(),
  automaticExecution: yup.boolean().required(),
  taskDefinitionOrder: yup.number().nullable().required(),
  scope: yup.object().shape({}),
  condition: yup.object().shape({}),
  actions: yup.object().shape({}),
});

const CreateTaskDrawer: FC<CreateTaskDrawerProps> = ({
  open,
  onClose,
  onOkClick,
  onCancelClick,
}) => {
  const [data] = usePMTaskDefinitionSlice(state => [state.data]);
  const { watch, setValue, getValues, trigger, formState, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      visibleForImagingSpecialist: false,
      description: '',
      automaticExecution: false,
      taskDefinitionOrder: data?.length + 1,
      scope: {},
      condition: {},
      actions: { actions: [] },
    },
  });
  const { errors, touchedFields, isValid, isDirty } = formState;
  const onChange = async (
    fieldName: keyof CreateReadingType,
    value: string | boolean
  ) => {
    setValue(fieldName, value, { shouldDirty: true, shouldTouch: true });
    trigger(fieldName);
  };

  const onBlur = (fieldName: keyof CreateReadingType, value: string) => {
    if (touchedFields[fieldName]) {
      onChange(fieldName, value.trim());
    }
  };
  useEffect(() => {
    if (data?.length) {
      setValue('taskDefinitionOrder', data?.length + 1);
    }
  }, [data, setValue]);
  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={'Create Task Definition'}
      open={open}
      width={464}
      placement='right'
      onClose={() => {
        onClose();
        reset();
      }}
      closeIcon={<CloseOutlined />}
    >
      <form className='overflow-auto px-1'>
        <Input
          required
          value={watch('name')}
          name={'name'}
          data-testid={'name'}
          onChange={e => onChange('name', e.target.value)}
          onBlur={e => onBlur('name', e.target.value)}
          errors={errors.name?.message}
          label={'Name'}
          placeholder={'Name'}
          classNameInputWrapper='mb-3'
          maxLength={50}
        />
        <Input
          required
          value={watch('code')}
          name={'code'}
          data-testid={'code'}
          onChange={e => onChange('code', e.target.value)}
          onBlur={e => onBlur('code', e.target.value)}
          errors={errors.code?.message}
          label={'Code'}
          placeholder={'Code'}
          classNameInputWrapper='mb-3'
          maxLength={50}
        />
        <TextArea
          className='!resize-none'
          label='Description'
          placeholder='Description'
          value={watch('description')}
          rows={3}
          name='description'
          onChange={e => onChange('description', e.target.value)}
          onBlur={e => onBlur('description', e.target.value)}
          maxLength={700}
          classNameInputWrapper='mb-3'
        />
        <Switch
          checked={watch('visibleForImagingSpecialist')}
          onChange={e => onChange('visibleForImagingSpecialist', e)}
          name={'unscheduledAllowed'}
          data-testid={'unscheduledAllowed'}
          label='Visible to Image specialist'
          extraLabel='Allow'
          classNameInputWrapper='flex justify-between items-center my-2 h-[3.2rem]'
        />
        <InputNumber
          value={watch('taskDefinitionOrder')}
          name={'taskDefinitionOrder'}
          data-testid={'taskDefinitionOrder'}
          onChange={value => {
            if (Number(value) > data?.length) {
              onChange('taskDefinitionOrder', (data.length + 1).toString());
              return;
            }
            onChange('taskDefinitionOrder', value as string);
          }}
          onBlur={e => onBlur('taskDefinitionOrder', e.target.value)}
          errors={errors.taskDefinitionOrder?.message}
          label={'Task Definition Order'}
          placeholder={'Task Definition Order'}
          className='mb-3'
          maxLength={50}
        />
        <Switch
          checked={watch('automaticExecution')}
          onChange={e => onChange('automaticExecution', e)}
          name={'automaticExecution'}
          data-testid={'automaticExecution'}
          label='Automatic Execution'
          extraLabel='Allow'
          classNameInputWrapper='flex justify-between items-center my-2 h-[3.2rem]'
        />
      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button
            data-testid={'cancel-btn'}
            className='w-[17rem]'
            onClick={() => {
              onCancelClick();
              reset();
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid={'ok-btn'}
            className='w-[17rem]'
            type='primary'
            disabled={!isValid || !isDirty}
            onClick={() => onOkClick(getValues())}
          >
            Proceed
          </Button>
        </div>
      </footer>
    </Drawer>
  );
};

export default CreateTaskDrawer;
